import { useEffect, useState } from "react";
import api, { formatIDR } from "@/lib/api";
import { AdminLayout } from "@/components/AdminLayout";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import { CurrencyDollar, Ticket, UsersThree, CalendarStar, Receipt } from "@phosphor-icons/react";

const COLORS = ["#00F0FF", "#FF0055", "#00FF66", "#FFCC00", "#A855F7"];

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-card border border-border/40 p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</span>
        <Icon size={22} weight="duotone" style={{ color: accent }} />
      </div>
      <p className="font-heading text-3xl font-black tracking-tight" style={{ color: accent }}>{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
  }, []);

  if (!stats) return <AdminLayout title="Dashboard"><div className="text-zinc-500">Memuat statistik...</div></AdminLayout>;

  return (
    <AdminLayout title="Dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="stats-grid">
        <Stat icon={CurrencyDollar} label="Pendapatan (Lunas)" value={formatIDR(stats.total_revenue)} accent="#00F0FF" />
        <Stat icon={Ticket} label="Tiket Terbit" value={stats.total_tickets} accent="#FF0055" />
        <Stat icon={UsersThree} label="Kehadiran" value={`${stats.total_attended} (${stats.attendance_rate}%)`} accent="#00FF66" />
        <Stat icon={Receipt} label="Pesanan Lunas / Menunggu" value={`${stats.total_orders} / ${stats.pending_orders ?? 0}`} accent="#FFCC00" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 bg-card border border-border/40 p-5">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-5">Pendapatan per Acara</h3>
          {stats.sales_by_event.length === 0 ? (
            <p className="text-zinc-600 text-sm py-16 text-center">Belum ada penjualan.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={stats.sales_by_event}>
                <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
                <XAxis dataKey="name" stroke="#666" fontSize={11} tickFormatter={(v) => v.slice(0, 12)} />
                <YAxis stroke="#666" fontSize={11} tickFormatter={(v) => `${v / 1000}k`} />
                <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", borderRadius: 0 }}
                  formatter={(v) => formatIDR(v)} cursor={{ fill: "rgba(0,240,255,0.05)" }} />
                <Bar dataKey="revenue" fill="#00F0FF" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-card border border-border/40 p-5">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-5">Distribusi Jenis Tiket</h3>
          {stats.ticket_distribution.length === 0 ? (
            <p className="text-zinc-600 text-sm py-16 text-center">Belum ada data.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={stats.ticket_distribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {stats.ticket_distribution.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", borderRadius: 0 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="bg-card border border-border/40">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-primary p-5 pb-3">Transaksi Terbaru</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="recent-orders-table">
            <thead>
              <tr className="text-left text-zinc-500 border-y border-border/40 text-xs uppercase tracking-wider">
                <th className="p-3 font-semibold">Pembeli</th>
                <th className="p-3 font-semibold">Acara</th>
                <th className="p-3 font-semibold">Metode</th>
                <th className="p-3 font-semibold">Total</th>
                <th className="p-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {stats.recent_orders.length === 0 ? (
                <tr><td colSpan={5} className="p-8 text-center text-zinc-600">Belum ada transaksi.</td></tr>
              ) : stats.recent_orders.map((o) => {
                const st = o.status === "paid"
                  ? { label: "Lunas", cls: "bg-[#00FF66]/15 text-[#00FF66]" }
                  : o.status === "pending"
                  ? { label: "Menunggu", cls: "bg-yellow-400/15 text-yellow-400" }
                  : o.status === "expired"
                  ? { label: "Kedaluwarsa", cls: "bg-zinc-600/30 text-zinc-400" }
                  : { label: "Gagal", cls: "bg-secondary/15 text-secondary" };
                const method = { midtrans: "Midtrans", stripe: "Stripe", ipaymu: "iPaymu" }[o.provider] || "-";
                return (
                  <tr key={o.id} className="border-b border-border/20 hover:bg-white/5">
                    <td className="p-3">{o.buyer_name}</td>
                    <td className="p-3 text-zinc-400">{o.event_name}</td>
                    <td className="p-3"><span className="text-[10px] uppercase tracking-wider bg-primary/15 text-primary px-2 py-0.5">{method}</span></td>
                    <td className="p-3 text-primary font-semibold">{formatIDR(o.total)}</td>
                    <td className="p-3"><span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${st.cls}`}>{st.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
